/**
 * Resolve deep-link path for an in-app notification.
 * Uses notification.type + notification.meta (written by backend triggers).
 *
 * @param {object} notification
 * @param {boolean} isPortal - staff portal vs owner
 * @returns {string|null} path to navigate, or null if no target
 */
export function getNotificationPath(notification, isPortal = false) {
  if (!notification) return null;

  const type = notification.type;
  const meta = notification.meta || {};

  // Explicit route from backend (future-proof)
  if (typeof meta.route === "string" && meta.route.startsWith("/")) {
    return meta.route;
  }

  const id = (v) => (v != null ? String(v) : null);

  switch (type) {
    case "LATE_CHECKIN":
      return isPortal ? "/staff-panel/attendance" : "/staff-attendance";

    case "VACANT_CLASS":
      return isPortal ? "/staff-panel/timetable" : "/timetable";

    case "FEE_APPROVAL": {
      const batchId = id(meta.batchId);
      const base = isPortal ? "/staff-panel/fees/batches" : "/fees/batches";
      return batchId ? `${base}?batchId=${batchId}` : base;
    }

    case "PAYROLL_APPROVAL": {
      const runId = id(meta.payrollRunId);
      if (runId) {
        return isPortal
          ? `/staff-panel/payroll-runs/${runId}`
          : `/payroll-runs/${runId}`;
      }
      return isPortal ? "/staff-panel/payroll-runs" : "/payroll-runs";
    }

    case "MARKS_SUBMITTED": {
      const assignmentId = id(meta.assignmentId);
      if (assignmentId) {
        return isPortal
          ? `/staff-panel/marks/entry/${assignmentId}`
          : `/marks/entry/${assignmentId}`;
      }
      return isPortal
        ? "/staff-panel/marks/my-assignments"
        : "/marks/assignments";
    }

    case "LEAVE_REQUEST":
      return isPortal
        ? "/staff-panel/leave-management"
        : "/leave-management";

    case "LEAVE_APPROVED":
    case "LEAVE_REJECTED":
      // Recipient is usually the requesting staff member
      return isPortal ? "/staff-panel/leaves" : "/leave-management";

    case "NOTICE_PUBLISHED": {
      const noticeId = id(meta.noticeId);
      const base = isPortal ? "/staff-panel/notices" : "/notices";
      return noticeId ? `${base}?noticeId=${noticeId}` : base;
    }

    case "NEW_MESSAGE": {
      const conversationId = id(meta.conversationId);
      const base = isPortal ? "/staff-panel/chat" : "/chat";
      return conversationId
        ? `${base}?conversationId=${conversationId}`
        : base;
    }

    default:
      return null;
  }
}

export default getNotificationPath;
